import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

// Helper to safely merge stored value with defaults, ensuring all required properties exist
function mergeWithDefaults<T>(storedValue: T | null, defaultValue: T): T {
  if (storedValue === null) {
    return defaultValue;
  }
  // If both are arrays, return the stored array (don't merge)
  if (Array.isArray(storedValue) && Array.isArray(defaultValue)) {
    return storedValue;
  }
  // If default is array but stored is not, return default
  if (Array.isArray(defaultValue) && !Array.isArray(storedValue)) {
    return defaultValue;
  }
  if (typeof storedValue !== 'object' || typeof defaultValue !== 'object') {
    return storedValue ?? defaultValue;
  }
  // Merge stored value with defaults, so any missing properties use defaults
  return { ...defaultValue, ...storedValue };
}

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState(() => {
    try {
      const storedValue = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      if (storedValue === null) {
        return defaultValue;
      }
      const parsed = JSON.parse(storedValue);
      // Merge with defaults to ensure all required properties exist
      return mergeWithDefaults(parsed, defaultValue);
    } catch (error) {
      // If parsing fails, return default value and clear corrupted data
      console.warn(`Failed to parse localStorage key "${key}":`, error);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key);
      }
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (typeof window !== 'undefined' && e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: any) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;
      if (typeof window !== 'undefined') localStorage.setItem(key, JSON.stringify(result));
      return result;
    });
  };

  // Returned as a tuple so callers destructure a typed value instead of the
  // value|setter union — otherwise every `stored.map((x, i) => …)` infers `any`.
  // The setter stays loose because call sites also pass functional updaters,
  // which the implementation above unwraps.
  return [value, setValueInLocalStorage] as [ValueType, (newValue: any) => void];
}
